import VisibleView from "components/hoc/VisibleView";
import { useCallback, useEffect, useRef, useState } from "react";
import Card from "./cards";
import Dial from "./dial";
import { DeviceInfo, WidgetProps } from "types";
import { fetchDetailDevices } from "service/gatewayService";
import { parseColorToHex } from "utils/functions";
import {
    SingleSensorChart,
    StackedChart,
    HeatmapChart,
    ConsumptionChart,
} from "./charts";
import ChartActionDropdown from "../ChartActionDropdown";

// Map of chart types to their corresponding components
const CHART_COMPONENT_MAP = {
    HEATMAP: HeatmapChart,
    SINGLE_LINE: SingleSensorChart,
    COMBINED: SingleSensorChart,
    TIME: SingleSensorChart,
    MULTIPLE_LINES: StackedChart,
    STACKED: StackedChart,
    BAR: ConsumptionChart,
    STACKED_BAR: ConsumptionChart,
} as const;

const getComponent = (_source: string, chart: string) => {
    const normalizedChart = chart.toUpperCase();

    if (normalizedChart.startsWith("CARD")) return Card;
    if (normalizedChart.startsWith("GAUGE")) return Dial;

    return (
        CHART_COMPONENT_MAP[
        normalizedChart as keyof typeof CHART_COMPONENT_MAP
        ] ?? null
    );
};

const SensorWidget = ({
    gatewaysConfig,
    zoomLevel,
    panel,
    panel: { attributes, source, chart },
    dashboardId,
    onDelete,
    allAlerts,
}: WidgetProps) => {
    const [sensorDetails, setSensorDetails] = useState<DeviceInfo[]>([]);
    const isMountedRef = useRef(true);
    const fetchDeviceInfo = useCallback(async () => {
        try {
            const params = attributes.map(
                ({ gateway_id, device_id, said }: any) => ({
                    gateway_id,
                    device_id,
                    said: (said ?? 0).toString(),
                })
            );

            const devices = await fetchDetailDevices(params);
            if (!isMountedRef.current) return;
            devices.forEach((d, index) => {
                d.chartColor = parseColorToHex(attributes[index].color);
                d.unit =
                    attributes[index].unit === "na"
                        ? d.unit
                        : attributes[index].unit;
            });
            setSensorDetails(devices);
        } catch {
            setSensorDetails([]);
        }
    }, [attributes]);

    useEffect(() => {
        isMountedRef.current = true;
        fetchDeviceInfo();
        return () => {
            isMountedRef.current = false;
        };
    }, [attributes, fetchDeviceInfo]);

    const Component = getComponent(source, chart);

    return (
        <>
            {Component && (
                <div className="position-relative h-100">
                    <ChartActionDropdown
                        panelId={panel.uuid}
                        dashboardId={dashboardId}
                        onDelete={onDelete}
                        panel={panel}
                        deviceConfig={sensorDetails}
                    />

                    <VisibleView>
                        <Component
                            gatewaysConfig={gatewaysConfig}
                            zoomLevel={zoomLevel}
                            panel={panel}
                            dashboardId={dashboardId}
                            onDelete={onDelete}
                            sensorDetails={sensorDetails}
                            allAlerts={allAlerts}
                        />
                    </VisibleView>
                </div>
            )}
        </>
    );
};

export default SensorWidget;
